import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["id", "type", "variant", "size", "iconName", "textClickable", "onClick", "children", "className", "spacing", "role", "tabIndex", "onIconKeyUp", "onIconKeyDown", "rootProps"],
  _excluded2 = ["aria-label"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/* © 2022 Promethean. All Rights Reserved.
 *
 * Unauthorized copying of this file or any part of this file
 * via any medium is strictly prohibited.
 */
import React from 'react';
import classnames from 'classnames';
import { useIntl } from 'react-intl';
import { StyledStatusIcon, StyledStatusIconItem, ROOT_PREFIX } from './statusIconStyles';
export var EmbersStatusIcon = function EmbersStatusIcon(props) {
  var id = props.id,
    type = props.type,
    _props$variant = props.variant,
    variant = _props$variant === void 0 ? 'outline' : _props$variant,
    _props$size = props.size,
    size = _props$size === void 0 ? 'medium' : _props$size,
    iconName = props.iconName,
    textClickable = props.textClickable,
    onClick = props.onClick,
    children = props.children,
    className = props.className,
    spacing = props.spacing,
    role = props.role,
    tabIndex = props.tabIndex,
    onIconKeyUp = props.onIconKeyUp,
    onIconKeyDown = props.onIconKeyDown,
    rootProps = props.rootProps,
    otherProps = _objectWithoutProperties(props, _excluded);
  var intl = useIntl();
  var handleIconClick = function handleIconClick(e) {
    e.preventDefault();
    onClick === null || onClick === void 0 || onClick(e);
  };
  var getAriaLabelAndRole = function getAriaLabelAndRole(icon) {
    var nameSeparated = icon.split('-');
    switch (nameSeparated === null || nameSeparated === void 0 ? void 0 : nameSeparated[1]) {
      case 'success':
      case 'check':
        return {
          'aria-label': props['aria-label'] || intl.formatMessage({
            id: 'embers.statusIcon.success',
            defaultMessage: 'success'
          }),
          role: 'img'
        };
      case 'error':
        return {
          'aria-label': props['aria-label'] || intl.formatMessage({
            id: 'embers.statusIcon.error',
            defaultMessage: 'error'
          }),
          role: 'img'
        };
      case 'more':
        if (nameSeparated[2] === 'horizontal') {
          return {
            'aria-label': props['aria-label'] || intl.formatMessage({
              id: 'embers.statusIcon.moreInfo',
              defaultMessage: 'more information'
            }),
            role: 'button'
          };
        }
        break;
      case 'wifi':
        switch (nameSeparated[2]) {
          case 'null':
            return {
              'aria-label': props['aria-label'] || intl.formatMessage({
                id: 'embers.statusIcon.wifiNull',
                defaultMessage: 'not yet connected'
              }),
              role: 'img'
            };
          case 'off':
            return {
              'aria-label': props['aria-label'] || intl.formatMessage({
                id: 'embers.statusIcon.wifiNotConnected',
                defaultMessage: 'not connected'
              }),
              role: 'img'
            };
          case 'on':
            return {
              'aria-label': props['aria-label'] || intl.formatMessage({
                id: 'embers.statusIcon.wifiConnected',
                defaultMessage: 'connected'
              }),
              role: 'img'
            };
          default:
            return {
              role: role,
              'aria-label': props['aria-label']
            };
        }
      default:
        return {
          role: props['aria-label'] ? role || 'img' : undefined,
          'aria-label': props['aria-label']
        };
    }
    return {
      role: props['aria-label'] ? role || 'img' : undefined,
      'aria-label': props['aria-label']
    };
  };
  var defaultIcon = function defaultIcon(type) {
    switch (type) {
      case 'alert':
      case 'error':
      case 'info':
        return _defineProperty(_defineProperty({}, "icon-".concat(type), variant === 'outline'), "icon-".concat(type, "-fill"), variant === 'filled');
      case 'success':
      case 'destructiveSuccess':
        return {
          'icon-completed': variant === 'outline',
          'icon-completed-fill': variant === 'filled'
        };
      case 'notification':
        return {
          'icon-notification': variant === 'outline',
          'icon-notification-fill': variant === 'filled'
        };
      case 'important':
        return {
          'icon-notification-important': variant === 'outline',
          'icon-notification-important-fill': variant === 'filled'
        };
      default:
        return {};
    }
  };
  var rootCls = classnames(_defineProperty(_defineProperty({}, ROOT_PREFIX, true), "".concat(ROOT_PREFIX, "-").concat(size), true), className);
  var iconCls = classnames(_objectSpread(_objectSpread(_defineProperty(_defineProperty(_defineProperty(_defineProperty({}, "".concat(ROOT_PREFIX, "-icon"), true), "".concat(ROOT_PREFIX, "-").concat(type, "-icon"), true), "".concat(ROOT_PREFIX, "-").concat(size), !children), "".concat(ROOT_PREFIX, "-icon-clickable"), typeof onClick === 'function'), iconName ? _defineProperty({}, iconName, iconName) : defaultIcon(type)), {}, _defineProperty({}, className, !children && className)));
  var ariaProps = getAriaLabelAndRole(iconName || "icon-".concat(type));
  var _ = otherProps['aria-label'],
    restProps = _objectWithoutProperties(otherProps, _excluded2);
  ariaProps = _objectSpread(_objectSpread({}, ariaProps), restProps);
  var iconProps = _objectSpread({
    id: id,
    className: iconCls,
    onClick: !textClickable ? handleIconClick : void 0,
    onKeyUp: onIconKeyUp,
    onKeyDown: onIconKeyDown,
    tabIndex: tabIndex
  }, ariaProps);
  return children ? /*#__PURE__*/React.createElement(StyledStatusIconItem, _extends({
    className: rootCls,
    "data-textclickable": textClickable || false,
    onClick: textClickable ? handleIconClick : void 0
  }, rootProps || {}), /*#__PURE__*/React.createElement("i", iconProps), /*#__PURE__*/React.createElement("span", {
    className: "".concat(ROOT_PREFIX, "-text ").concat(ROOT_PREFIX, "-").concat(type, "-text"),
    style: spacing ? {
      marginLeft: spacing
    } : {}
  }, children)) : /*#__PURE__*/React.createElement(StyledStatusIcon, iconProps);
};